import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

const GRID = "#EDE7DC"
const MUTED = "#7A7570"

// Multi-dataset line/bar chart (optionally stacked). Datasets and formatting come from values.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    type: { type: String, default: "line" },
    labels: Array,
    datasets: Array,
    stacked: { type: Boolean, default: false },
    unit: { type: String, default: "raw" },
    fill: { type: Boolean, default: false },
    legend: { type: Boolean, default: true },
    beginAtZero: { type: Boolean, default: false },
  }

  connect() {
    const fmt = (v) => this.format(v)
    const isBar = this.typeValue === "bar"
    const dense = this.labelsValue.length > 40

    const datasets = this.datasetsValue.map((d) => {
      const pr = d.pr || []
      return {
        label: d.label,
        data: d.data,
        pr, // kept for the tooltip callback below
        borderColor: d.color,
        backgroundColor: isBar ? d.color : this.fillValue ? this.rgba(d.color, 0.15) : "transparent",
        borderWidth: isBar ? 0 : 2.5,
        tension: 0.35,
        fill: this.fillValue,
        spanGaps: true,
        // PR weeks get a bigger hollow dot (white fill, thicker coloured ring) so they pop.
        pointRadius: isBar ? 0 : d.data.map((_, i) => (pr[i] ? 6 : dense ? 0 : 3)),
        pointHoverRadius: isBar ? 0 : d.data.map((_, i) => (pr[i] ? 8 : 5)),
        pointBackgroundColor: "#fff",
        pointBorderColor: d.color,
        pointBorderWidth: d.data.map((_, i) => (pr[i] ? 3 : 2)),
        maxBarThickness: 28,
      }
    })

    this.chart = new Chart(this.canvasTarget, {
      type: this.typeValue,
      data: { labels: this.labelsValue, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: this.legendValue, labels: { color: MUTED, boxWidth: 12, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const base = `${ctx.dataset.label ? ctx.dataset.label + ": " : ""}${fmt(ctx.parsed.y)}`
                return ctx.dataset.pr?.[ctx.dataIndex] ? `${base} · 🏆 PR` : base
              },
            },
          },
        },
        scales: {
          x: {
            stacked: this.stackedValue,
            grid: { display: false },
            ticks: { maxTicksLimit: dense ? 8 : 13, color: MUTED, font: { size: 10 } },
          },
          y: {
            stacked: this.stackedValue,
            beginAtZero: isBar || this.beginAtZeroValue,
            grace: "8%", // headroom so top points / PR stars aren't clipped
            grid: { color: GRID },
            ticks: { color: MUTED, font: { size: 10 }, callback: fmt },
          },
        },
      },
    })
  }

  format(v) {
    if (v == null) return ""
    if (this.unitValue === "tonnes") return `${(v / 1000).toFixed(1)}t`
    if (this.unitValue === "kg") return `${Math.round(v)} kg`
    if (this.unitValue === "min") return `${Math.round(v)}m`
    if (this.unitValue === "sec") return `${Math.round(v)}s`
    return `${Math.round(v)}`
  }

  rgba(hex, alpha) {
    const n = parseInt(hex.replace("#", ""), 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
  }

  disconnect() {
    this.chart?.destroy()
  }
}
